// MenuSearchEntidades.js
import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import { useTable, useFilters } from "react-table";
import MDButton from "components/MDButton";

const DEFAULT_MAX_FATURAS = 20;

const mesesLabels = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

export default function MenuSearchEntidades({ open, onClose, entidade }) {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(false); // tabela
  const [linhas, setLinhas] = useState([]);
  const [maxFaturas, setMaxFaturas] = useState(DEFAULT_MAX_FATURAS);

  // preços por material (apenas alguns perfis)
  const [precosOpen, setPrecosOpen] = useState(false);
  const [precosLoading, setPrecosLoading] = useState(false);
  const [precos, setPrecos] = useState(null);
  const [precosRefMat, setPrecosRefMat] = useState("");

  // gráfico
  const [vendasResumo, setVendasResumo] = useState([]);
  const [loadingResumo, setLoadingResumo] = useState(false);

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  // Quem pode ver valores monetários
  const podeVerValores =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Comercial");

  // Quem pode ver o botão VER PREÇOS (igual a podeVerValores mas SEM Comercial)
  const podeVerPrecos =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  // refvendedor do utilizador corrente (se for Comercial)
  const refVendedorAtual = temFuncao("Comercial") ? (userData?.refvendedor || "").trim() : "";

  // -------------------- Carregar dados --------------------
  useEffect(() => {
    let active = true;

    const carregar = async () => {
      if (!open || !entidade) return;

      const refentidade = (entidade.ref || entidade.refentidade || "").trim();
      if (!refentidade) return;

      // só avançamos se maxFaturas for um inteiro positivo
      const n = parseInt(maxFaturas, 10);
      if (!Number.isFinite(n) || n <= 0) return;

      // reset locais
      setLinhas([]);
      setVendasResumo([]);

      setLoading(true);
      setLoadingResumo(true);

      // ---- 1) dados para o gráfico (resumo) ----
      const [dadosResumo, errResumo] = await handleApi(
        api.post("Entidades/SelVendasPorEntidadeResumo", {
          refentidade,
          refvendedor: refVendedorAtual,
        })
      );

      if (!active) return;

      setLoadingResumo(false);

      if (errResumo) {
        showAlert(errResumo, "error");
        setVendasResumo([]);
      } else {
        setVendasResumo(Array.isArray(dadosResumo) ? dadosResumo : []);
      }

      // ---- 2) linhas de faturas (tabela) ----
      const [dados, err] = await handleApi(
        api.post("Entidades/SelVendasPorEntidade", {
          refentidade,
          refvendedor: refVendedorAtual,
          maxfaturas: n,
        })
      );

      if (!active) return;

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        setLinhas([]);
        return;
      }

      setLinhas(Array.isArray(dados) ? dados : []);
    };

    if (open && entidade) {
      carregar();
    } else if (!open) {
      setLinhas([]);
      setVendasResumo([]);
      setLoading(false);
      setLoadingResumo(false);
    }

    return () => {
      active = false;
    };
  }, [open, entidade, maxFaturas, refVendedorAtual, showAlert, temFuncao]);

  // -------------------- Helpers --------------------

  const titulo = entidade
    ? `Faturas de ${entidade.nome || ""} (${entidade.ref ?? ""})`
    : "Faturas da entidade";

  const formatData = (v) => {
    if (!v) return "";
    const s = String(v);
    return s.substring(0, 10);
  };

  const formatNum = (v, dec = 2) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("pt-PT", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
  };

  const joinValPerc = (val, perc, withEuro = false) => {
    const v = formatNum(val);
    const p = formatNum(perc, 1);
    let s = "";
    if (v) s += `${v}${withEuro ? " €" : ""}`;
    if (p) s += `${s ? " " : ""}(${p}%)`;
    return s;
  };

  const calcularMargem = (precosObj) => {
    if (!precosObj) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const venda = Number(precosObj.zpr0 ?? 0);
    const custo = Number(precosObj.zpvr ?? 0);

    if (!Number.isFinite(venda) || !Number.isFinite(custo) || custo === 0) {
      return { margemValor: null, margemPerc: null, margemTexto: "" };
    }

    const margemValor = venda - custo;
    // margem % calculada sobre o custo (mais intuitivo: 100% = vender ao dobro do custo)
    const margemPerc = (margemValor / custo) * 100;

    return {
      margemValor,
      margemPerc,
      margemTexto: joinValPerc(margemValor, margemPerc, true), // "X,XX € (Y,Y%)"
    };
  };

  const abrirPrecosMaterial = async (refMat) => {
    const ref = (refMat || "").trim();
    if (!ref) return;

    setPrecosRefMat(ref);
    setPrecos(null);
    setPrecosOpen(true);
    setPrecosLoading(true);

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMaterialPrecos", { refmaterial: ref })
    );

    setPrecosLoading(false);

    if (err) {
      showAlert(err, "error");
      setPrecos(null);
      return;
    }

    console.log("dados", dados);

    setPrecos(dados); // objeto único vindo da API
  };

  const handleChangeMax = (e) => {
    const v = e.target.value;
    // permitimos vazio para o utilizador apagar e escrever de novo
    if (v === "") {
      setMaxFaturas("");
      return;
    }
    // apenas dígitos
    if (!/^\d+$/.test(v)) return;
    setMaxFaturas(v);
  };

  const maxFaturasInt = parseInt(maxFaturas || DEFAULT_MAX_FATURAS, 10) || DEFAULT_MAX_FATURAS;

  // -------------------- Dados do gráfico (últimos 3 anos, por mês) --------------------

  const anoAtual = new Date().getFullYear();
  const anosVendas = [anoAtual, anoAtual - 1, anoAtual - 2];

  const vendasPorAno = {};
  anosVendas.forEach((ano) => {
    vendasPorAno[ano] = new Array(12).fill(0);
  });

  (vendasResumo || []).forEach((row) => {
    const ano = Number(row.ano);
    const mes = Number(row.mes); // 1-12
    const qtd = Number(row.quantidade ?? row.qtd ?? 0);

    const mesIdx = mes - 1;
    if (!Number.isFinite(qtd)) return;
    if (!vendasPorAno[ano]) return;
    if (mesIdx < 0 || mesIdx > 11) return;

    vendasPorAno[ano][mesIdx] += qtd;
  });

  // arredondar e remover meses futuros do ano corrente
  Object.keys(vendasPorAno).forEach((anoStr) => {
    const ano = Number(anoStr);
    vendasPorAno[ano] = (vendasPorAno[ano] || []).map((v) => {
      if (v === null || typeof v !== "number" || Number.isNaN(v)) return v;
      return Math.round(v);
    });
  });

  const mesAtual = new Date().getMonth(); // 0-11
  for (let m = mesAtual + 1; m < 12; m += 1) {
    if (vendasPorAno[anoAtual]) {
      vendasPorAno[anoAtual][m] = null;
    }
  }

  const vendasDatasets = anosVendas.map((ano, idx) => ({
    label: String(ano),
    data: vendasPorAno[ano],
    color:
      idx === 0
        ? "warning" // ano corrente
        : idx === 1
        ? "primary" // ano anterior
        : "secondary", // antepenúltimo
  }));

  const vendasDatasetsFiltrados = vendasDatasets.filter((ds) =>
    (ds.data || []).some((v) => Number.isFinite(v) && v !== 0)
  );

  const vendasTotaisAno = vendasDatasetsFiltrados.reduce((acc, ds) => {
    const total = (ds.data || []).reduce((s, v) => (Number.isFinite(v) ? s + v : s), 0);
    acc[ds.label] = total;
    return acc;
  }, {});

  const temVendasResumo = vendasDatasetsFiltrados.length > 0;

  const vendasChart = {
    labels: mesesLabels,
    datasets: vendasDatasetsFiltrados.map((ds) => ({
      label: ds.label,
      data: ds.data,
      color: ds.color,
    })),
  };

  // -------------------- react-table (dados + colunas) --------------------

  const data = useMemo(() => {
    return (linhas || []).map((row) => {
      const qtd = row.qtd ?? row.quantidade;
      const qtdStr = formatNum(qtd, 2);
      const unidade = row.qtdunid || "";

      return {
        data: formatData(row.faturadata),
        faturanumero: row.faturanumero ?? "",
        item: row.item ?? "",
        itemTipo: row.catitemnome ?? "",
        refMat: row.refmaterial ?? "",
        nomeMat: row.nomematerial ?? "",
        qtdUnid: `${qtdStr}${unidade ? ` (${unidade})` : ""}`,
        precoAcons: formatNum(row.precoaconselhado),
        descCom: joinValPerc(row.desccomvalor, row.desccomperc),
        descProm: joinValPerc(row.descpromvalor, row.descpromperc),
        precoLiq: formatNum(row.precoliquido),
        iva: joinValPerc(row.ivavalor, row.ivaperc),
        precoTotal: formatNum(row.precototal),
        fabricante: row.fabricante ?? "",
      };
    });
  }, [linhas]);

  const columns = useMemo(() => {
    const base = [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 110,
        Cell: ({ row }) => (
          <MDButton
            color="warning"
            onClick={() => abrirPrecosMaterial(row.original.refMat)}
            sx={{ flex: "0 0 auto" }}
          >
            VER PREÇOS
          </MDButton>
        ),
      },

      {
        Header: "Data",
        accessor: "data",
      },
      {
        Header: "Nº Fatura",
        accessor: "faturanumero",
      },
      {
        Header: "Item Nº",
        accessor: "item",
        align: "right",
      },
      {
        Header: "Item Tipo",
        accessor: "itemTipo",
      },
      {
        Header: "Refª Mat.",
        accessor: "refMat",
      },
      {
        Header: "Nome do material",
        accessor: "nomeMat",
      },
      {
        Header: "Qtd. (Unid.)",
        accessor: "qtdUnid",
        align: "right",
      },
    ];

    if (podeVerValores) {
      base.push(
        {
          Header: () => (
            <Tooltip title="Preço Aconselhado" arrow>
              <span>Preço Acons.</span>
            </Tooltip>
          ),
          accessor: "precoAcons",
          align: "right",
        },
        {
          Header: () => (
            <Tooltip title="Desconto Comercial" arrow>
              <span>Desc. Com.</span>
            </Tooltip>
          ),
          accessor: "descCom",
          align: "right",
        },
        {
          Header: () => (
            <Tooltip title="Desconto Promocional" arrow>
              <span>Desc. Prom.</span>
            </Tooltip>
          ),
          accessor: "descProm",
          align: "right",
        },
        {
          Header: "Preço Líq.",
          accessor: "precoLiq",
          align: "right",
        },
        {
          Header: "IVA",
          accessor: "iva",
          align: "right",
        },
        {
          Header: "Preço Total",
          accessor: "precoTotal",
          align: "right",
        }
      );
    }

    base.push({
      Header: "Fabricante",
      accessor: "fabricante",
    });

    return base;
  }, [podeVerValores, podeVerPrecos]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters
  );

  const { margemTexto } = calcularMargem(precos);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>{titulo}</DialogTitle>

        <DialogContent dividers>
          {/* ================== GRÁFICO ================== */}
          <h6 style={{ marginTop: 0, marginBottom: 8, fontSize: 13, color: "#7b809a" }}>
            Vendas da entidade (quantidades, nos últimos 3 anos)
          </h6>

          {loadingResumo ? (
            <div
              style={{
                padding: "16px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : !temVendasResumo ? (
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
              Não há vendas registadas para esta entidade nos últimos 3 anos
              {temFuncao("Comercial") && refVendedorAtual ? " com o seu código de vendedor." : "."}
            </div>
          ) : (
            <div style={{ marginBottom: 20 }}>
              {/* legenda das cores/anos */}
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  marginBottom: 8,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 20,
                }}
              >
                {vendasDatasetsFiltrados.map((ds) => (
                  <span key={ds.label} style={{ display: "inline-flex", alignItems: "center" }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        marginRight: 6,
                        backgroundColor: colorMap[ds.color] || "#999",
                      }}
                    />
                    {ds.label}{" "}
                    <span style={{ color: "#3a3a3a", marginLeft: 4 }}>
                      (Qtd. total:
                      {Number(vendasTotaisAno[ds.label]).toLocaleString("pt-PT", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      )
                    </span>
                  </span>
                ))}
              </div>

              <DefaultLineChart title="" description="" chart={vendasChart} />
            </div>
          )}

          {/* Linha com nº de faturas */}
          <div
            style={{
              marginBottom: 8,
              fontSize: 12,
              color: "#7b809a",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span>
              Apresentadas as últimas
              <span style={{ marginLeft: 5, marginRight: 5 }}>
                <input
                  type="text"
                  value={maxFaturas}
                  onChange={handleChangeMax}
                  style={{
                    width: 40,
                    padding: "6px 6px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    textAlign: "center",
                  }}
                />
              </span>
              faturas de vendas (todas as linhas, ordenadas por data desc.).
            </span>
          </div>

          {/* separador visual entre gráfico e tabela */}
          <div
            style={{
              height: 1,
              backgroundColor: "#eee",
              marginBottom: 8,
            }}
          />

          {/* ================== TABELA ================== */}
          {loading ? (
            <div
              style={{
                padding: "24px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={26} />
            </div>
          ) : linhas.length === 0 ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontradas faturas de vendas para esta entidade
              {temFuncao("Comercial") && refVendedorAtual ? " com o seu código de vendedor." : "."}
            </div>
          ) : (
            <div
              style={{
                maxHeight: 420,
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table
                {...getTableProps()}
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  minWidth: 900,
                }}
              >
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          key={column.id}
                          {...column.getHeaderProps()}
                          style={{
                            ...thStyle,
                            textAlign: column.align || "left",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          <div>{column.render("Header")}</div>
                          {column.canFilter && (
                            <div style={{ marginTop: 4 }}>{column.render("Filter")}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr key={row.id} {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            key={cell.column.id}
                            {...cell.getCellProps()}
                            style={{
                              ...tdStyle,
                              textAlign: cell.column.align || "left",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="info" variant="gradient" size="small" onClick={onClose}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog de preços por material (apenas se tiver permissão) */}
      <Dialog open={precosOpen} onClose={() => setPrecosOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preços do material {precosRefMat ? `(${precosRefMat})` : ""}</DialogTitle>

        <DialogContent dividers>
          {precosLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <CircularProgress size={26} />
            </div>
          ) : !precos ? (
            <div style={{ fontSize: 13, color: "#555" }}>
              Não foram encontrados preços registados para este material.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                minWidth: 400,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>PV Acons.</th>
                  {podeVerPrecos && <th style={thStyle}>Margem</th>}
                  {podeVerPrecos && <th style={thStyle}>PC</th>}
                </tr>
              </thead>

              <tbody>
                <tr>
                  {/* PV aconselhado (zpr0) */}
                  <td style={tdStyle}>{formatNum(precos.zpr0)}</td>

                  {/* Margem em € e % (calculada a partir de zpr0 e zpvr) */}
                  {podeVerPrecos && <td style={tdStyle}>{margemTexto}</td>}

                  {/* Preço de custo (zpvr, já calculado dinamicamente na API para certos fabricantes) */}
                  {podeVerPrecos && <td style={tdStyle}>{formatNum(precos.zpvr)}</td>}
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="info"
            variant="gradient"
            size="small"
            onClick={() => setPrecosOpen(false)}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

// estilos reutilizáveis
const thStyle = {
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  borderBottom: "1px solid #eee",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  borderTop: "1px solid #eee",
  fontSize: 13,
};

// filtro padrão (mesmo critério do Playbook)
function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        fontSize: 11,
        padding: "2px 4px",
        borderRadius: 4,
        border: "1px solid #ddd",
      }}
      placeholder="filtrar…"
    />
  );
}
